import { useState } from "react";
import {
  useDelete,
  useNotify,
  usePermissions,
  useRefresh,
  useUpdate,
  type RaRecord,
} from "react-admin";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { formatListDate } from "@/utils/date-format";
import { hasPermission } from "@/utils/permission-utils";
import RazaRequestCreateLagatDialog from "../RazaRequestCreateLagatDialog";
import RazaRequestLinkLagatDialog from "../RazaRequestLinkLagatDialog";

export type RazaEventLagatSummary = {
  id: string;
  receiptNo: string | null;
  amount: number | null;
  receiptDate: string | Date | null;
  paymentMode: string | null;
  paymentRef: string | null;
};

function formatInr(amount: number | null | undefined): string {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "INR" }).format(
    Number(amount)
  );
}

type RazaRequestEventLagatBlockProps = {
  razaRequestId: string;
  record: RaRecord;
  eventId: string;
  eventPurpose?: string | null;
  lagat: RazaEventLagatSummary | null | undefined;
};

export function RazaRequestEventLagatBlock({
  razaRequestId,
  record,
  eventId,
  eventPurpose,
  lagat,
}: RazaRequestEventLagatBlockProps) {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading: unlinking }] = useUpdate();
  const [deleteReceipt, { isLoading: deleting }] = useDelete();
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);

  const canCreate = hasPermission(permissions, "lagatReceipts.create");
  const canView = hasPermission(permissions, "lagatReceipts.view");
  const canEdit = hasPermission(permissions, "lagatReceipts.edit");
  const canDelete = hasPermission(permissions, "lagatReceipts.delete");

  const busy = unlinking || deleting;

  const unlinkFromEventOnly = () => {
    if (!lagat?.id || !canEdit) return;
    update(
      "lagatReceipts",
      {
        id: lagat.id,
        data: { razaEventId: null },
        previousData: {
          id: lagat.id,
          razaRequestId,
          razaEventId: eventId,
        } as RaRecord,
      },
      {
        onSuccess: () => {
          setPermanentDeleteDialogOpen(false);
          setUnlinkConfirmOpen(false);
          notify("Receipt unlinked from this event; it remains as general lagat on the request.", {
            type: "success",
          });
          refresh();
        },
        onError: (err) => {
          notify(err instanceof Error ? err.message : "Update failed", { type: "error" });
        },
      }
    );
  };

  const deleteReceiptPermanently = () => {
    if (!lagat?.id || !canDelete) return;
    deleteReceipt(
      "lagatReceipts",
      { id: lagat.id, previousData: { id: lagat.id } as RaRecord },
      {
        onSuccess: () => {
          setPermanentDeleteDialogOpen(false);
          setUnlinkConfirmOpen(false);
          notify("Receipt deleted", { type: "success" });
          refresh();
        },
        onError: (err) => {
          setPermanentDeleteDialogOpen(false);
          notify(err instanceof Error ? err.message : "Delete failed", { type: "error" });
        },
      }
    );
  };

  const viewReceipt = (id: string) => {
    window.open(`#/lagat-rcpt/${id}`, "_blank");
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Lagat for this event
      </Typography>
      {lagat?.id ? (
        <Stack spacing={1} sx={{ mb: 1 }}>
          <Typography variant="body2">
            {lagat.receiptNo?.trim() || lagat.id} · {formatInr(lagat.amount)} ·{" "}
            {formatListDate(lagat.receiptDate, { empty: "—" })}
            {lagat.paymentMode?.trim() ? ` · ${lagat.paymentMode.trim()}` : null}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {canView ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() => viewReceipt(lagat.id)}
                startIcon={<DownloadIcon fontSize="small" />}
              >
                View receipt
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                component={RouterLink}
                to={`/lagatReceipts/${lagat.id}/edit`}
                size="small"
                variant="outlined"
              >
                Open receipt
              </Button>
            ) : null}
            {canEdit || canDelete ? (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                onClick={() => {
                  setPermanentDeleteDialogOpen(false);
                  setUnlinkConfirmOpen(true);
                }}
                disabled={busy}
              >
                Unlink or delete…
              </Button>
            ) : null}
          </Stack>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No lagat is tied to this event yet. At most one receipt per event.
        </Typography>
      )}
      {!lagat?.id ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          {canCreate ? (
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={() => setCreateOpen(true)}
            >
              Create lagat
            </Button>
          ) : null}
          {canEdit ? (
            <Button variant="outlined" size="small" onClick={() => setLinkOpen(true)}>
              Link existing receipt
            </Button>
          ) : null}
        </Stack>
      ) : null}

      <RazaRequestLinkLagatDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        razaRequestId={razaRequestId}
        defaultRazaEventId={eventId}
      />
      {createOpen ? (
        <RazaRequestCreateLagatDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          razaRequestId={razaRequestId}
          record={record}
          defaultRazaEventId={eventId}
          defaultEventPurpose={eventPurpose ?? null}
        />
      ) : null}

      <Dialog
        open={unlinkConfirmOpen}
        onClose={() => {
          if (!busy) {
            setPermanentDeleteDialogOpen(false);
            setUnlinkConfirmOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lagat for this event</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText component="div">
              Unlink from the event to keep the receipt on this request as{" "}
              <strong>general lagat</strong>, or delete the receipt entirely if you no longer need
              it.
            </DialogContentText>
            {canEdit ? (
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => unlinkFromEventOnly()}
                  disabled={busy}
                  fullWidth
                >
                  {unlinking ? "Updating…" : "Unlink from event only"}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  Clears the event link; the receipt stays on this request under general lagat.
                </Typography>
              </Stack>
            ) : null}
            {canDelete ? (
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => setPermanentDeleteDialogOpen(true)}
                  disabled={busy}
                  fullWidth
                >
                  {deleting ? "Deleting…" : "Delete receipt"}
                </Button>
                <Typography variant="caption" color="text.secondary" display="block">
                  Soft-deletes the receipt record (same as delete on the lagat list).
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPermanentDeleteDialogOpen(false);
              setUnlinkConfirmOpen(false);
            }}
            disabled={busy}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={permanentDeleteDialogOpen && unlinkConfirmOpen}
        onClose={() => {
          if (!deleting) setPermanentDeleteDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete receipt permanently</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this lagat receipt permanently? This cannot be undone from here.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermanentDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={() => deleteReceiptPermanently()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
